import { type Ref, useRef } from 'react';
import { type AriaButtonProps, mergeProps, useButton, useFocusRing } from 'react-aria';

import { mergeRefs } from '../../../lib/mergeRefs';
import { Glyph } from '../../common/Glyph';
import { Tooltip } from '../../common/tooltip/Tooltip';

import { type GlyphType } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';
import styles from './MainNavActionButton.module.css';

const HINT_DELAY_MS = 300;

type CoreGlyph = GlyphType['core'][keyof GlyphType['core']];

interface MainNavActionButtonProps extends AriaButtonProps<'button'> {
  glyphSource: CoreGlyph;
  /** Accessible name of the button. */
  'aria-label': string;
  /** Keyboard shortcut that triggers the same action, e.g. `Meta+K`. */
  'aria-keyshortcuts'?: string;
  /** Text shown in the tooltip next to the button. */
  hint: string;
}

/**
 * Round icon button in the bottom section of the main navigation rail. Shows a hint tooltip
 * beside it on hover and keyboard focus.
 */
export function MainNavActionButton(props: MainNavActionButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const button = useButton(props, buttonRef);
  const focusRing = useFocusRing();

  function renderTrigger(
    triggerRef: Ref<HTMLButtonElement>,
    triggerProps: Record<string, unknown>,
  ) {
    return (
      <button
        {...mergeProps(triggerProps, button.buttonProps, focusRing.focusProps)}
        ref={mergeRefs([triggerRef, buttonRef])}
        className={styles.root}
        aria-keyshortcuts={props['aria-keyshortcuts']}
        data-is-pressed={button.isPressed}
        data-is-focus-visible={focusRing.isFocusVisible}
      >
        <Glyph
          source={props.glyphSource}
          size={theme.icon.size_medium}
          color={'var(--module-glyph-color)'}
        />
      </button>
    );
  }

  return (
    <Tooltip placement={'end'} delay={HINT_DELAY_MS} renderTrigger={renderTrigger}>
      <div className={styles.hint}>{props.hint}</div>
    </Tooltip>
  );
}
